import {
  BanknoteIcon,
  AlertCircleIcon,
  LayoutGridIcon,
  ArrowUpIcon,
  CheckCircleIcon,
} from 'lucide-react'
import type { DashboardSummary } from '@/lib/api'

function CardFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5 transition-shadow hover:shadow-md">
      {children}
    </div>
  )
}

function CardLabel({ icon: Icon, children }: { icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="size-5 shrink-0 text-[var(--brand-mint)]" />
      <span className="text-sm text-muted-foreground">{children}</span>
    </div>
  )
}

function CardValue({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`text-2xl font-semibold tabular-nums ${className}`}>
      {children}
    </span>
  )
}

function CardExplanation({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs text-muted-foreground/80 leading-relaxed">
      {children}
    </p>
  )
}

export function OutstandingCard({ summary }: { summary: DashboardSummary }) {
  const symbol = summary.currency_symbol || '$'
  const totalOutstanding = Number(summary.total_outstanding)
  const hasOverdue = summary.overdue_cycles > 0

  return (
    <CardFrame>
      <div className="flex items-center justify-between">
        <CardLabel icon={BanknoteIcon}>Total outstanding liability</CardLabel>
        {hasOverdue && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600 dark:bg-red-950/30 dark:text-red-400">
            <AlertCircleIcon className="size-3" />
            {summary.overdue_cycles} overdue
          </span>
        )}
      </div>
      <CardValue>
        {symbol}{Number(totalOutstanding).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </CardValue>
      <CardExplanation>
        What your organization currently owes across{' '}
        <strong>{summary.pending_cycles + summary.overdue_cycles}</strong> cycle{summary.pending_cycles + summary.overdue_cycles !== 1 ? 's' : ''}.
        {' '}{summary.pending_cycles} pending{summary.overdue_cycles > 0 ? `, ${summary.overdue_cycles} overdue` : ''}.
        {' '}This is the total sum of all unpaid obligations to employees and participants.
      </CardExplanation>
    </CardFrame>
  )
}

export function OverdueAmountCard({ summary }: { summary: DashboardSummary }) {
  const symbol = summary.currency_symbol || '$'
  const overdueAmount = Number(summary.overdue_amount)
  const hasOverdue = summary.overdue_cycles > 0

  return (
    <CardFrame>
      <div className="flex items-center justify-between">
        <CardLabel icon={AlertCircleIcon}>Total overdue</CardLabel>
        {hasOverdue && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600 dark:bg-red-950/30 dark:text-red-400">
            <AlertCircleIcon className="size-3" />
            {summary.overdue_cycles} overdue
          </span>
        )}
      </div>
      <CardValue className={hasOverdue ? 'text-red-600 dark:text-red-400' : ''}>
        {symbol}{overdueAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </CardValue>
      <CardExplanation>
        {!hasOverdue
          ? 'No overdue cycles. Your organization is up to date on all payment cycle obligations.'
          : (
            <>
              <strong>{symbol}{overdueAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> is overdue across{' '}
              <strong>{summary.overdue_cycles}</strong> cycle{summary.overdue_cycles !== 1 ? 's' : ''}.
              {' '}This is the total amount past due — paying these promptly avoids disruptions for your employees.
            </>
          )}
      </CardExplanation>
    </CardFrame>
  )
}

export function AverageCircleCard({ summary }: { summary: DashboardSummary }) {
  const symbol = summary.currency_symbol || '$'
  const avgSize = Number(summary.avg_circle_size)

  return (
    <CardFrame>
      <CardLabel icon={LayoutGridIcon}>Average Circle size</CardLabel>
      <CardValue>
        {symbol}{avgSize.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </CardValue>
      <CardExplanation>        {summary.open_circles_count === 0
          ? 'No open Circles yet. Average Circle size will appear once employees start creating Funding Circle requests.'
          : (
            <>
              The average Funding Circle request across{' '}
              <strong>{summary.open_circles_count}</strong>
              {' '} open Circle{summary.open_circles_count === 1 ? '' : 's'} is{' '}
              <strong>{symbol}{avgSize.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>.
              {' '}This helps you gauge typical funding requests and plan your payroll budget accordingly.
            </>
          )
      }
      </CardExplanation>
    </CardFrame>
  )
}

export function MaxCircleCard({ summary }: { summary: DashboardSummary }) {
  const symbol = summary.currency_symbol || '$'
  const maxSize = Number(summary.max_circle_size)

  return (
    <CardFrame>
      <CardLabel icon={ArrowUpIcon}>Largest open Circle</CardLabel>
      <CardValue>
        {symbol}{maxSize.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </CardValue>
      <CardExplanation>        {summary.open_circles_count === 0
          ? 'No open Circles yet. The largest request will appear once employees start creating Funding Circle requests.'
          : (
            <>
              The largest open Funding Circle request is{' '}
              <strong>{symbol}{maxSize.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
              {(summary.max_circle_title ?? summary.max_circle_creator_name) && (
                <strong>
                  {summary.max_circle_title
                    ? ` — ${summary.max_circle_title}`
                    : ` — ${summary.max_circle_creator_name}'s Circle`}
                </strong>
              )}
              .{' '}This is a risk indicator — unusually large requests may need closer review before funding.
            </>
          )
      }
      </CardExplanation>
    </CardFrame>
  )
}

export function CompletionRateCard({ summary }: { summary: DashboardSummary }) {
  const rateColor =
    summary.total_cycles === 0
      ? 'text-muted-foreground'
      : summary.completion_rate >= 80
        ? 'text-emerald-600 dark:text-emerald-400'
        : summary.completion_rate >= 50
          ? 'text-amber-600 dark:text-amber-400'
          : 'text-red-600 dark:text-red-400'

  return (
    <CardFrame>
      <CardLabel icon={CheckCircleIcon}>Cycle completion rate</CardLabel>
      <div className="flex items-baseline gap-2">
        <CardValue className={rateColor}>
          {summary.total_cycles === 0 ? '—' : `${summary.completion_rate}%`}
        </CardValue>
        {summary.total_cycles > 0 && (
          <span className="text-xs text-muted-foreground/70">
            {summary.completed_cycles} of {summary.total_cycles} cycles
          </span>
        )}
      </div>
      {/* Mini progress bar */}
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            summary.total_cycles === 0
              ? 'bg-muted-foreground/20'
              : summary.completion_rate >= 80
                ? 'bg-emerald-500'
                : summary.completion_rate >= 50
                  ? 'bg-amber-500'
                  : 'bg-red-500'
          }`}
          style={{
            width: summary.total_cycles === 0 ? '0%' : `${summary.completion_rate}%`,
          }}
        />
      </div>
      <CardExplanation>
        {summary.total_cycles === 0
          ? 'No payment cycles have been created yet. Cycle completion tracking will begin once the first cycle is due.'
          : (
            <>
              <strong>{summary.completed_cycles}</strong> of{' '}
              <strong>{summary.total_cycles}</strong> payment cycle{summary.total_cycles !== 1 ? 's' : ''} have been completed on time.
              {' '}The completion rate measures how consistently your organization meets payment deadlines across all payroll cycles.
            </>
          )}
      </CardExplanation>
    </CardFrame>
  )
}
